/**
 * Путь запроса для телеметрии — шаблон, а не сам адрес (PRIV-3).
 *
 * <p>Шаги отчёта (`GET /api/… → 200`) раньше писались с полным путём, а двенадцать последних
 * прикладываются к каждому отчёту об ошибке: оттуда в журнал бэкенда, в GlitchTip и в Telegram
 * уезжали поисковые запросы (`messages/search?q=…`, `users/search?q=…`), каждая ссылка из
 * открытых чатов (`link-preview?url=…`) и секреты встреч (`/meetings/join/<секрет>`, `/m/<секрет>`).
 *
 * <p>Здесь остаётся только то, что нужно для разбора поломки, — какая ручка:
 * <ul>
 *   <li>схема и хост отбрасываются — адрес сервера известен и так;
 *   <li>query-строка и `#…` отбрасываются целиком — именно в них поиск и ссылки;
 *   <li>сегмент после `/meetings/join/` и `/m/` — секрет — становится `:secret`;
 *   <li>идентификаторы (UUID, числа, длинные токены с цифрами) становятся `:id`.
 * </ul>
 * Так же поступает серверный `RequestLogFilter`: он пишет `getRequestURI()` без query.
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Ключ хранилища, токен приглашения, base64-кусок: длинное и с цифрами — не имя ручки. */
const TOKEN_RE = /^(?=.*\d)[A-Za-z0-9._~%+=-]{12,}$/;
/** После этих сегментов идёт секрет: показывать нельзя ни целиком, ни частью. */
const SECRET_AFTER = new Set(['join', 'm', 'invite', 'invites']);

function maskSegment(seg: string, prev: string | undefined): string {
  if (!seg) return seg;
  if (prev !== undefined && SECRET_AFTER.has(prev.toLowerCase())) return ':secret';
  if (UUID_RE.test(seg) || /^\d+$/.test(seg) || TOKEN_RE.test(seg)) return ':id';
  return seg;
}

/** Шаблон пути: `/api/chats/:id/messages`. Принимает и относительный путь, и полный адрес. */
export function telemetryPath(pathOrUrl: string): string {
  try {
    let s = String(pathOrUrl ?? '').trim();
    // Схема и хост: `https://vrmsgr.pro/api/x` → `/api/x`.
    s = s.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/?#]*/i, '');
    const cut = s.search(/[?#]/);
    if (cut >= 0) s = s.slice(0, cut);
    const parts = s.split('/');
    for (let i = 0; i < parts.length; i += 1) {
      let seg = parts[i];
      try {
        seg = decodeURIComponent(seg);
      } catch {
        // битая %-последовательность — смотрим на сегмент как есть
      }
      const masked = maskSegment(seg, i > 0 ? parts[i - 1] : undefined);
      parts[i] = masked === seg ? parts[i] : masked;
    }
    return parts.join('/');
  } catch {
    return '';
  }
}

/**
 * Похоже на путь или адрес внутри свободного текста: `схема://…` или `/…` в начале строки либо
 * после пробела, скобки, кавычки, `=`. Без lookbehind — не на всех движках он есть.
 */
const PATHLIKE_RE = /(^|[\s(["'=])([a-z][a-z0-9+.-]*:\/\/[^\s"'<>]+|\/[^\s"'<>]+)/gi;

/**
 * Свободный текст для телеметрии — шаг, текст ошибки, значение контекста: каждый путь или адрес
 * внутри приводится к шаблону {@link telemetryPath}. Это страховка на уровне самого журнала:
 * кто бы ни положил туда адрес, query и секреты до сервера не доедут.
 */
export function scrubTelemetryText(text: string): string {
  try {
    return String(text).replace(PATHLIKE_RE, (_m, lead: string, path: string) => {
      // Хвостовая пунктуация к пути не относится: «… /api/x).»
      const tail = /[).,;:!]+$/.exec(path)?.[0] ?? '';
      const core = tail ? path.slice(0, -tail.length) : path;
      return lead + telemetryPath(core) + tail;
    });
  } catch {
    return '';
  }
}
